import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import Footer from "./Footer";
import GlobalSearch from "./GlobalSearch";
import AssistantWidget from "./AssistantWidget";
import {
  Home, Users, FolderKanban, Handshake, Wrench, ClipboardList, LayoutTemplate, Ruler, Grid3x3, LayoutGrid,
  Package, FileText, Images, Star, LifeBuoy, BarChart3, Settings, Bell, Search, Building2, UsersRound, Coins,
  ChevronDown, Menu, X, PanelLeftClose, PanelLeft, Sun, Moon, ShieldCheck, FlaskConical, CalendarClock, Target,
} from "lucide-react";

// Structure fidèle à la maquette Cockpit
const NAV_MAIN = [
  { to: "/", label: "Accueil", icon: Home, end: true },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/prospects", label: "Prospects", icon: Target },
  { to: "/projets", label: "Projets", icon: FolderKanban },
  { to: "/sous-traitance", label: "Sous-traitance", icon: Handshake },
  { to: "/atelier", label: "Atelier", icon: Wrench },
];
const NAV_TOOLS = [
  { to: "/etudes", label: "Études & prises de cotes", icon: ClipboardList },
  { to: "/gabarits", label: "Gabarits", icon: LayoutTemplate },
  { to: "/calculateur", label: "Calcul matière", icon: Ruler },
  { to: "/imposition", label: "Imposition", icon: Grid3x3 },
];
const NAV_MORE = [
  { to: "/modeles", label: "Projets types", icon: LayoutTemplate },
  { to: "/matieres", label: "Matières & stocks", icon: Package },
  { to: "/documents", label: "Devis & factures", icon: FileText },
  { to: "/depenses", label: "Dépenses", icon: Coins },
  { to: "/realisations", label: "Réalisations", icon: Images },
  { to: "/avis-clients", label: "Avis clients", icon: Star },
  { to: "/experts", label: "Experts", icon: LifeBuoy },
  { to: "/statistiques", label: "Statistiques", icon: BarChart3 },
  { to: "/parametres", label: "Compte FABRIKO", icon: Settings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("fabriko_sidebar") === "1");
  const toggleCollapse = () => setCollapsed((c) => { const n = !c; localStorage.setItem("fabriko_sidebar", n ? "1" : "0"); return n; });

  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const go = (item) => {
    setMobileOpen(false);
    if (item.soon) toast.info(`« ${item.label} » — module bientôt disponible`);
  };

  const Item = ({ item }) => {
    const Icon = item.icon;
    const base = `group flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-200 ${collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"}`;
    if (item.soon) {
      return (
        <button onClick={() => go(item)} title={collapsed ? item.label : undefined} data-testid={`nav-soon-${item.label}`}
          className={`${base} w-full text-slate-400 hover:text-white hover:bg-white/5`}>
          <Icon className="w-[18px] h-[18px] shrink-0" />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </button>
      );
    }
    return (
      <NavLink to={item.to} end={item.end} onClick={() => go(item)} title={collapsed ? item.label : undefined}
        data-testid={`nav-${item.to === "/" ? "dashboard" : item.to.slice(1)}`}
        className={({ isActive }) => `${base} ${isActive ? "bg-gradient-to-r from-cognac-400 to-cognac-500 text-white shadow-lg shadow-cognac-500/25" : "text-slate-300 hover:text-white hover:bg-white/5"}`}>
        <Icon className="w-[18px] h-[18px] shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </NavLink>
    );
  };

  const sidebarW = collapsed ? "w-[76px]" : "w-64";
  const mainMl = collapsed ? "md:ml-[76px]" : "md:ml-64";

  return (
    <div className="min-h-screen bg-background">
      <AssistantWidget />
      {mobileOpen && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-overlay" />}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 ${sidebarW} flex flex-col bg-gradient-to-b from-[#131b33] to-[#0b1224] text-slate-300 transform transition-all duration-300 md:translate-x-0 ${mobileOpen ? "translate-x-0 w-64" : "-translate-x-full"}`}>
        {/* Logo + tagline */}
        <div className={`relative py-4 flex items-center border-b border-white/5 ${collapsed ? "justify-center px-2" : "px-5"}`}>
          {collapsed ? (
            <div className="h-9 w-9 rounded-lg flex items-center justify-center" data-testid="sidebar-logo"><LayoutGrid className="w-7 h-7 text-white" strokeWidth={2.4} /></div>
          ) : (
            <div data-testid="sidebar-logo">
              <img src="/fabriko-wordmark.webp" alt="Fabriko" className="h-[72px] w-auto max-w-full object-contain" />
              <div className="mt-1 text-[10px] text-slate-400 tracking-wide">L'atelier en meilleure main</div>
            </div>
          )}
          <button onClick={() => setMobileOpen(false)} className="md:hidden absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-white/10 text-slate-400" data-testid="close-sidebar-btn"><X className="w-5 h-5" /></button>
        </div>

        <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto scrollbar-thin overflow-x-hidden">
          {NAV_MAIN.map((it) => <Item key={it.label} item={it} />)}

          {/* OUTILS MÉTIER — panneau encadré */}
          <div className={`mt-3 ${collapsed ? "" : "rounded-2xl bg-white/[0.03] border border-white/5 p-2"}`}>
            {!collapsed && <div className="px-2 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-cognac-300/80 flex items-center gap-2"><span className="w-3 h-px bg-cognac-400/60" />Outils métier</div>}
            {collapsed && <div className="mx-2 my-2 h-px bg-white/5" />}
            <div className="space-y-1">{NAV_TOOLS.map((it) => <Item key={it.label} item={it} />)}</div>
          </div>

          <div className="pt-2 space-y-1">
            {NAV_MORE.map((it) => <Item key={it.label} item={it} />)}
            {(user?.network_admin || user?.role === "fabriko_admin") && <Item item={{ to: "/reseau-admin", label: "Admin Réseau STS", icon: ShieldCheck }} />}
            {user?.role === "expert" && <Item item={{ to: "/agenda-expert", label: "Mon Agenda", icon: CalendarClock }} />}
          </div>
        </nav>

        {/* Patch cuir + signature (bas-gauche) */}
        {!collapsed && (
          <div className="relative px-3 pb-4 pt-1 shrink-0" data-testid="leather-signature">
            <div className="leather relative h-[88px] rounded-2xl rotate-[-1.5deg] overflow-hidden">
              {/* couture inset */}
              <div className="absolute inset-[6px] rounded-xl border-[1.5px] border-dashed border-[#EBDDC6]/55" />
              {/* rivets / trous de couture */}
              <span className="absolute top-2.5 left-2.5 w-[5px] h-[5px] rounded-full bg-black/45 shadow-[inset_0_1px_1px_rgba(255,255,255,0.25)]" />
              <span className="absolute top-2.5 right-2.5 w-[5px] h-[5px] rounded-full bg-black/45 shadow-[inset_0_1px_1px_rgba(255,255,255,0.25)]" />
              <span className="absolute bottom-2.5 left-2.5 w-[5px] h-[5px] rounded-full bg-black/45 shadow-[inset_0_1px_1px_rgba(255,255,255,0.25)]" />
              <span className="absolute bottom-2.5 right-2.5 w-[5px] h-[5px] rounded-full bg-black/45 shadow-[inset_0_1px_1px_rgba(255,255,255,0.25)]" />
              <div className="signature absolute inset-0 flex items-center justify-center text-center text-[#F3E7D2] text-[23px] leading-[1.05] px-5 drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]">
                Des savoir-faire durables
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className={`${mainMl} flex flex-col min-h-screen min-w-0 transition-all duration-300`}>
        <header className="h-16 backdrop-blur-md bg-background/85 border-b border-border/60 flex items-center gap-2 sm:gap-3 px-3 sm:px-5 lg:px-6 sticky top-0 z-30">
          <button onClick={() => setMobileOpen(true)} className="md:hidden p-2 rounded-lg hover:bg-secondary text-slate-600 shrink-0"><Menu className="w-5 h-5" /></button>
          <button onClick={toggleCollapse} className="hidden md:inline-flex p-2 rounded-lg hover:bg-secondary text-slate-500 shrink-0" data-testid="sidebar-collapse-btn" title={collapsed ? "Déplier" : "Replier"}>
            {collapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>

          {/* Search — centré */}
          <button onClick={() => setSearchOpen(true)} data-testid="global-search"
            className="hidden md:flex items-center gap-2 flex-1 max-w-2xl mx-auto px-4 py-2.5 rounded-xl bg-secondary/80 text-sm text-slate-400 hover:bg-slate-200/70 transition-colors">
            <Search className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left">Rechercher un client, un projet, une facture…</span>
            <kbd className="text-[10px] font-mono bg-card border border-border rounded px-1.5 py-0.5 text-slate-500">Ctrl K</kbd>
          </button>
          <div className="flex-1 md:hidden" />

          <button onClick={toggle} className="p-2 rounded-lg hover:bg-secondary text-slate-500 shrink-0" data-testid="theme-toggle-btn" title={theme === "dark" ? "Mode clair" : "Mode sombre"}>
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button className="relative p-2 rounded-lg hover:bg-secondary shrink-0" data-testid="notifications-btn">
            <Bell className="w-5 h-5 text-slate-500" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500" />
          </button>

          {/* Atelier dropdown (pill) */}
          <button className="hidden sm:inline-flex items-center gap-2 pl-2.5 pr-2 py-1.5 rounded-xl border border-border bg-card hover:bg-secondary transition-colors shrink-0" data-testid="tenant-pill">
            <Building2 className="w-4 h-4 text-cognac-500" />
            <span className="text-sm font-semibold text-navy truncate max-w-[120px]">{user?.company_name || "Mon atelier"}</span>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>

          {/* Voir le réseau */}
          <button onClick={() => navigate("/reseau")} data-testid="see-network-btn"
            className="hidden lg:inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep transition-colors shrink-0">
            <UsersRound className="w-4 h-4" /> Voir le réseau des artisans <ChevronDown className="w-3.5 h-3.5 opacity-70" />
          </button>

          {/* Avatar */}
          <button onClick={() => navigate("/parametres")} className="flex items-center gap-1.5 shrink-0" data-testid="user-menu">
            <div className="w-9 h-9 rounded-full bg-slate-300 text-slate-700 flex items-center justify-center font-display font-bold text-sm">{(user?.name || "?").slice(0, 1).toUpperCase()}</div>
            <ChevronDown className="w-4 h-4 text-slate-400 hidden sm:block" />
          </button>
          <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn" className="p-2 rounded-lg hover:bg-secondary text-slate-400 shrink-0" title="Déconnexion"><X className="w-4 h-4" /></button>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          {user?.company?.is_demo && (
            <div className="mb-5 rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 px-4 py-3 flex items-center gap-3" data-testid="demo-banner">
              <div className="w-9 h-9 rounded-xl bg-amber-400 flex items-center justify-center shrink-0"><FlaskConical className="w-5 h-5 text-white" /></div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-amber-900 dark:text-amber-300">Environnement de démonstration</div>
                <div className="text-xs text-amber-700 dark:text-amber-400/80">Données fictives — aucun débit réel. Réinitialisez depuis les Paramètres.</div>
              </div>
            </div>
          )}
          <Outlet />
        </main>
        <Footer />
      </div>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
